import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Typography,
    Card,
    CardContent,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Grid,
    LinearProgress,
    Avatar,
    AvatarGroup,
    Tooltip,
    Pagination,
    Stack,
    Switch,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
    FormControl,
    InputLabel,
    Select
} from '@mui/material';
import {
    TrendingUp,
    Assignment,
    Timeline,
    Add,
    Edit,
    Delete,
    Visibility,
    Folder,
    MoreVert,
    Info,
    GroupAdd,
    AssignmentTurnedIn,
    Article
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import UserStoryAndTaskTable from './UserStoryAndTaskTable';

// Mock data for epics
const mockEpicsData = [
    {
        id: 1,
        name: 'Share-Ur-Truck App',
        description: 'Complete mobile app for truck sharing platform',
        project: 'Mobile Development',
        status: 'In Progress',
        priority: 'High',
        progress: 75,
        startDate: '2024-01-15',
        endDate: '2024-06-30',
        totalStories: 12,
        completedStories: 9,
        assignees: ['JD', 'MP', 'AL'],
        color: '#2196f3',
        manager: { first_name: 'John', last_name: 'Doe' },
        team_leader: { first_name: 'Mike', last_name: 'Parker' },
        clients: [{ first_name: 'Client', last_name: 'One' }]
    },
    {
        id: 2,
        name: 'CommuniLink Beta',
        description: 'Communication platform for enterprise teams',
        project: 'Web Development',
        status: 'Todo',
        priority: 'Medium',
        progress: 0,
        startDate: '2024-03-01',
        endDate: '2024-08-15',
        totalStories: 8,
        completedStories: 0,
        assignees: ['SM', 'AL'],
        color: '#4caf50',
        manager: { first_name: 'Sarah', last_name: 'Miller' },
        team_leader: { first_name: 'Alex', last_name: 'Lee' },
        clients: [{ first_name: 'Client', last_name: 'Two' }]
    },
    {
        id: 3,
        name: 'Payment Gateway Integration',
        description: 'Integrate multiple payment providers',
        project: 'Backend Services',
        status: 'In Progress',
        priority: 'High',
        progress: 45,
        startDate: '2024-02-10',
        endDate: '2024-04-30',
        totalStories: 6,
        completedStories: 3,
        assignees: ['MP', 'RK'],
        color: '#ff9800',
        manager: { first_name: 'John', last_name: 'Doe' },
        team_leader: { first_name: 'Robert', last_name: 'King' },
        clients: [{ first_name: 'Client', last_name: 'Three' }]
    },
    {
        id: 4,
        name: 'Analytics Dashboard',
        description: 'Real-time analytics and reporting',
        project: 'Data Visualization',
        status: 'Completed',
        priority: 'Medium',
        progress: 100,
        startDate: '2024-01-01',
        endDate: '2024-03-15',
        totalStories: 10,
        completedStories: 10,
        assignees: ['JD', 'SM', 'RK'],
        color: '#9c27b0',
        manager: { first_name: 'Sarah', last_name: 'Miller' },
        team_leader: { first_name: 'Mike', last_name: 'Parker' },
        clients: [{ first_name: 'Client', last_name: 'Four' }]
    },
    {
        id: 5,
        name: 'User Authentication System',
        description: 'Secure authentication with MFA',
        project: 'Security',
        status: 'Todo',
        priority: 'Medium',
        progress: 0,
        startDate: '2024-04-01',
        endDate: '2024-05-31',
        totalStories: 5,
        completedStories: 0,
        assignees: ['AL'],
        color: '#f44336',
        manager: { first_name: 'John', last_name: 'Doe' },
        team_leader: { first_name: 'Alex', last_name: 'Lee' },
        clients: [{ first_name: 'Client', last_name: 'Five' }]
    },
    {
        id: 6,
        name: 'Mobile App Redesign',
        description: 'UI/UX overhaul for mobile applications',
        project: 'Design',
        status: 'In Progress',
        priority: 'Low',
        progress: 30,
        startDate: '2024-03-15',
        endDate: '2024-07-31',
        totalStories: 15,
        completedStories: 5,
        assignees: ['MP', 'AL', 'SM'],
        color: '#00bcd4',
        manager: { first_name: 'Sarah', last_name: 'Miller' },
        team_leader: { first_name: 'Robert', last_name: 'King' },
        clients: [{ first_name: 'Client', last_name: 'Six' }]
    }
];

// Mock my epics data (filtered for current user)
const mockMyEpicsData = [
    {
        id: 1,
        name: 'Share-Ur-Truck App',
        description: 'Complete mobile app for truck sharing platform',
        project: 'Mobile Development',
        status: 'In Progress',
        priority: 'High',
        progress: 75,
        startDate: '2024-01-15',
        endDate: '2024-06-30',
        totalStories: 12,
        completedStories: 9,
        assignees: ['JD', 'MP', 'AL'],
        color: '#2196f3',
        manager: { first_name: 'John', last_name: 'Doe' },
        team_leader: { first_name: 'Mike', last_name: 'Parker' },
        clients: [{ first_name: 'Client', last_name: 'One' }]
    },
    {
        id: 3,
        name: 'Payment Gateway Integration',
        description: 'Integrate multiple payment providers',
        project: 'Backend Services',
        status: 'In Progress',
        priority: 'High',
        progress: 45,
        startDate: '2024-02-10',
        endDate: '2024-04-30',
        totalStories: 6,
        completedStories: 3,
        assignees: ['MP', 'RK'],
        color: '#ff9800',
        manager: { first_name: 'John', last_name: 'Doe' },
        team_leader: { first_name: 'Robert', last_name: 'King' },
        clients: [{ first_name: 'Client', last_name: 'Three' }]
    }
];

const StyledCard = styled(Card)(({ theme }) => ({
    height: '100%',
    borderRadius: '8px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
    transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
    cursor: 'pointer',
    '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)'
    }
}));

const StatusChip = styled(Chip)(({ theme, status }) => ({
    fontWeight: 500,
    fontSize: '0.65rem',
    height: 20,
    ...(status === 'Todo' && {
        backgroundColor: '#fff3e0',
        color: '#f57c00'
    }),
    ...(status === 'In Progress' && {
        backgroundColor: '#e3f2fd',
        color: '#1976d2'
    }),
    ...(status === 'Completed' && {
        backgroundColor: '#e8f5e9',
        color: '#2e7d32'
    }),
    ...(status === 'Overdue' && {
        backgroundColor: '#ffebee',
        color: '#d32f2f'
    })
}));

const PriorityChip = styled(Chip)(({ theme, priority }) => ({
    fontWeight: 500,
    fontSize: '0.65rem',
    height: 20,
    ...(priority === 'High' && {
        backgroundColor: '#ffebee',
        color: '#d32f2f'
    }),
    ...(priority === 'Medium' && {
        backgroundColor: '#fff3e0',
        color: '#f57c00'
    }),
    ...(priority === 'Low' && {
        backgroundColor: '#e8f5e9',
        color: '#2e7d32'
    })
}));

export const EpicsComponentParent = ({ onProjectSelect, onViewTasks }) => {
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [showMyEpics, setShowMyEpics] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [showUserStoryTaskTable, setShowUserStoryTaskTable] = useState(false);
    const [selectedEpicForTable, setSelectedEpicForTable] = useState(null);
    
    // Menu state
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedEpic, setSelectedEpic] = useState(null);

    // Calculate card data based on mock data
    const calculateCardData = () => {
        const allEpics = showMyEpics ? mockMyEpicsData : mockEpicsData;
        
        return [
            {
                title: 'All Epics',
                count: allEpics.length,
                icon: <TrendingUp sx={{ fontSize: 24, color: 'white' }} />,
                gradient: 'linear-gradient(135deg, #6366F1 0%, #A855F7 100%)',
                key: 'all'
            },
            {
                title: 'Todo',
                count: allEpics.filter(epic => epic.status === 'Todo').length,
                icon: <Assignment sx={{ fontSize: 24, color: 'white' }} />,
                gradient: 'linear-gradient(135deg, #FF6B6B 0%, #FFE66D 100%)',
                key: 'todo'
            },
            {
                title: 'In Progress',
                count: allEpics.filter(epic => epic.status === 'In Progress').length,
                icon: <Timeline sx={{ fontSize: 24, color: 'white' }} />,
                gradient: 'linear-gradient(135deg, #4ECDC4 0%, #556270 100%)',
                key: 'ongoing'
            },
            {
                title: 'Completed',
                count: allEpics.filter(epic => epic.status === 'Completed').length,
                icon: <Folder sx={{ fontSize: 24, color: 'white' }} />,
                gradient: 'linear-gradient(135deg, #00b09b 0%, #96c93d 100%)',
                key: 'completed'
            }
        ];
    };

    const [cardData, setCardData] = useState(calculateCardData());

    useEffect(() => {
        setCardData(calculateCardData());
    }, [showMyEpics]);

    const handleCardClick = (cardKey) => {
        setSelectedCategory(cardKey);
        setCurrentPage(1);
    };

    const handleShowUserStoryTaskTable = (epic) => {
        setSelectedEpicForTable(epic);
        setShowUserStoryTaskTable(true);
    };

    const handleBackFromUserStoryTaskTable = () => {
        setShowUserStoryTaskTable(false);
        setSelectedEpicForTable(null);
    };

    const getFilteredEpics = () => {
        const epics = showMyEpics ? mockMyEpicsData : mockEpicsData;
        
        if (selectedCategory === 'all') {
            return epics;
        } else {
            const statusMap = {
                'todo': 'Todo',
                'ongoing': 'In Progress',
                'completed': 'Completed'
            };
            return epics.filter(epic => epic.status === statusMap[selectedCategory]);
        }
    };

    // Menu handlers
    const handleMenuOpen = (event, epic) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
        setSelectedEpic(epic);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setSelectedEpic(null);
    };

    const handleEdit = (epicId) => {
        console.log('Edit epic:', epicId);
        handleMenuClose();
    };

    const handleDelete = (epicId) => {
        console.log('Delete epic:', epicId);
        handleMenuClose();
    };

    const handleViewEpicDetails = (epicId) => {
        console.log('View epic details:', epicId);
        handleMenuClose();
    };

    const handleAssignTeam = (epic) => {
        if (onProjectSelect) {
            onProjectSelect(epic);
        }
        handleMenuClose();
    };

    // Menu items renderer
    const renderMenuItems = () => {
        const userType = localStorage.getItem('userType') || 'Manager';
        
        if (userType === 'Team Lead') {
            return (
                <>
                    <MenuItem
                        onClick={() => {
                            onViewTasks(selectedEpic?.id);
                            handleMenuClose();
                        }}
                    >
                        <ListItemIcon>
                            <AssignmentTurnedIn sx={{ fontSize: 16, color: '#10b981' }} />
                        </ListItemIcon>
                        <ListItemText>View Tasks</ListItemText>
                    </MenuItem>
                    <MenuItem
                        onClick={() => handleViewEpicDetails(selectedEpic?.id)}
                    >
                        <ListItemIcon>
                            <Info sx={{ fontSize: 16, color: '#3b82f6' }} />
                        </ListItemIcon>
                        <ListItemText>View Epic Details</ListItemText>
                    </MenuItem>
                </>
            );
        } else {
            // Manager/Admin menu
            return (
                <>
                    <MenuItem
                        onClick={(e) => {
                            e.stopPropagation();
                            handleAssignTeam(selectedEpic);
                        }}
                    >
                        <ListItemIcon>
                            <GroupAdd sx={{ fontSize: 16, color: '#6366f1' }} />
                        </ListItemIcon>
                        <ListItemText>Assign Team</ListItemText>
                    </MenuItem>

                    <MenuItem
                        onClick={() => {
                            onViewTasks(selectedEpic?.id);
                            handleMenuClose();
                        }}
                    >
                        <ListItemIcon>
                            <AssignmentTurnedIn sx={{ fontSize: 16, color: '#10b981' }} />
                        </ListItemIcon>
                        <ListItemText>View Tasks</ListItemText>
                    </MenuItem>

                    <MenuItem
                        onClick={() => handleViewEpicDetails(selectedEpic?.id)}
                    >
                        <ListItemIcon>
                            <Info sx={{ fontSize: 16, color: '#3b82f6' }} />
                        </ListItemIcon>
                        <ListItemText>View Epic Details</ListItemText>
                    </MenuItem>

                    <MenuItem
                        onClick={(e) => {
                            e.stopPropagation();
                            handleEdit(selectedEpic?.id);
                        }}
                    >
                        <ListItemIcon>
                            <Edit sx={{ fontSize: 16, color: '#64748b' }} />
                        </ListItemIcon>
                        <ListItemText>Edit</ListItemText>
                    </MenuItem>

                    <MenuItem
                        onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(selectedEpic?.id);
                        }}
                        sx={{ color: '#ef4444' }}
                    >
                        <ListItemIcon>
                            <Delete sx={{ fontSize: 16, color: '#ef4444' }} />
                        </ListItemIcon>
                        <ListItemText>Delete</ListItemText>
                    </MenuItem>
                </>
            );
        }
    };

    const filteredEpics = getFilteredEpics();
    const totalPages = Math.ceil(filteredEpics.length / rowsPerPage);
    const paginatedEpics = filteredEpics.slice(
        (currentPage - 1) * rowsPerPage,
        currentPage * rowsPerPage
    );

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { 
            month: 'short', 
            day: 'numeric',
            year: 'numeric'
        });
    };

    const getFirstLetter = (name) => {
        return name?.charAt(0)?.toUpperCase() || '';
    };

    if (showUserStoryTaskTable && selectedEpicForTable) {
        return (
            <UserStoryAndTaskTable 
                onBack={handleBackFromUserStoryTaskTable}
                projectId={selectedEpicForTable.id}
                projectName={selectedEpicForTable.name}
            />
        );
    }

    return (
        <Box sx={{ p: 1, height: 'calc(100vh - 100px)', display: 'flex', flexDirection: 'column' }}>
            {/* Header Section - More Compact */}
            <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between',
                alignItems: { xs: 'start', sm: 'center' },
                mb: 2,
                gap: 1
            }}>
                <Typography variant="h5" sx={{ fontWeight: 600, color: '#1e293b' }}>
                    Epics Overview
                </Typography>
                
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    flexDirection: { xs: 'column', sm: 'row' }
                }}>
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        px: 1,
                        py: 0.5,
                        borderRadius: 1,
                        bgcolor: '#f8fafc'
                    }}>
                        <Typography
                            sx={{
                                mr: 0.5,
                                fontSize: '0.85rem',
                                color: showMyEpics ? '#64748b' : '#3b82f6',
                                fontWeight: showMyEpics ? 400 : 500,
                            }}
                        >
                            All
                        </Typography>
                        <Switch
                            size="small"
                            checked={showMyEpics}
                            onChange={(e) => setShowMyEpics(e.target.checked)}
                            color="primary"
                        />
                        <Typography
                            sx={{
                                ml: 0.5,
                                fontSize: '0.85rem',
                                color: showMyEpics ? '#3b82f6' : '#64748b',
                                fontWeight: showMyEpics ? 500 : 400,
                            }}
                        >
                            My
                        </Typography>
                    </Box>
                    
                    <Button
                        variant="contained"
                        size="small"
                        startIcon={<Add />}
                        sx={{
                            backgroundColor: '#3b82f6',
                            textTransform: 'none',
                            fontWeight: 500,
                            px: 2,
                            py: 0.5,
                            fontSize: '0.85rem',
                            '&:hover': { backgroundColor: '#2563eb' }
                        }}
                    >
                        New Epic
                    </Button>
                </Box>
            </Box>

            {/* Compact Stats Cards */}
            <Grid container spacing={1.5} sx={{ mb: 2 }}>
                {cardData.map((card) => (
                    <Grid item xs={6} sm={3} key={card.key}>
                        <StyledCard 
                            onClick={() => handleCardClick(card.key)}
                            sx={{
                                background: card.gradient,
                                border: selectedCategory === card.key ? '1.5px solid #3b82f6' : 'none'
                            }}
                        >
                            <CardContent sx={{ p: 1.5 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Box>
                                        <Typography variant="h5" sx={{ color: 'white', fontWeight: 600, mb: 0.5, fontSize: '1.5rem' }}>
                                            {card.count}
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: 'white', opacity: 0.9, fontSize: '0.8rem' }}>
                                            {card.title}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ 
                                        bgcolor: 'rgba(255, 255, 255, 0.2)', 
                                        borderRadius: '50%',
                                        p: 1,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}>
                                        {card.icon}
                                    </Box>
                                </Box>
                            </CardContent>
                        </StyledCard>
                    </Grid>
                ))}
            </Grid>

            {/* Controls Row */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.85rem' }}>
                    Showing {paginatedEpics.length} of {filteredEpics.length} epics
                </Typography>
                <FormControl size="small" sx={{ minWidth: 100 }}>
                    <InputLabel sx={{ fontSize: '0.85rem' }}>Rows</InputLabel>
                    <Select
                        value={rowsPerPage}
                        onChange={(e) => {
                            setRowsPerPage(parseInt(e.target.value, 10));
                            setCurrentPage(1);
                        }}
                        label="Rows"
                        sx={{ fontSize: '0.85rem', height: '32px' }}
                    >
                        <MenuItem value={5} sx={{ fontSize: '0.85rem' }}>5</MenuItem>
                        <MenuItem value={10} sx={{ fontSize: '0.85rem' }}>10</MenuItem>
                        <MenuItem value={25} sx={{ fontSize: '0.85rem' }}>25</MenuItem>
                        <MenuItem value={50} sx={{ fontSize: '0.85rem' }}>50</MenuItem>
                    </Select>
                </FormControl>
            </Box>

            {/* Compact Epics Table */}
            <TableContainer 
                component={Paper} 
                sx={{ 
                    borderRadius: 1, 
                    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                    flex: 1,
                    overflow: 'auto',
                    '& .MuiTable-root': {
                        tableLayout: 'fixed'
                    }
                }}
            >
                <Table size="small">
                    <TableHead sx={{ bgcolor: '#f8fafc' }}>
                        <TableRow sx={{ height: '40px' }}>
                            {['Epic Name', 'Project', 'Status', 'Priority', 'Progress', 'Timeline', 'Stories', 'Team', 'Actions'].map((header) => (
                                <TableCell
                                    key={header}
                                    sx={{ 
                                        fontWeight: 600, 
                                        fontSize: '0.8rem',
                                        py: 1,
                                        borderBottom: '1px solid #e2e8f0',
                                        ...(header === 'Epic Name' && { width: '22%' }),
                                        ...(header === 'Project' && { width: '12%' }),
                                        ...(header === 'Status' && { width: '10%' }),
                                        ...(header === 'Priority' && { width: '10%' }),
                                        ...(header === 'Progress' && { width: '12%' }),
                                        ...(header === 'Timeline' && { width: '12%' }),
                                        // ...(header === 'Stories' && { width: '8%' }),
                                        ...(header === 'Team' && { width: '8%' }),
                                        ...(header === 'Actions' && { width: '6%' }),
                                    }}
                                >
                                    {header}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {paginatedEpics.map((epic) => (
                            <TableRow 
                                key={epic.id}
                                onClick={() => handleShowUserStoryTaskTable(epic)}
                                sx={{ 
                                    '&:hover': { bgcolor: '#f8fafc' },
                                    cursor: 'pointer',
                                    height: '52px'
                                }}
                            >
                                <TableCell sx={{ py: 1 }}>
                                    <Box>
                                        <Typography variant="body2" fontWeight={500} noWrap sx={{ fontSize: '0.85rem' }}>
                                            {epic.name}
                                        </Typography>
                                        <Typography variant="caption" color="textSecondary" sx={{ display: 'block', fontSize: '0.75rem' }} noWrap>
                                            {epic.description}
                                        </Typography>
                                    </Box>
                                </TableCell>
                                <TableCell sx={{ py: 1 }}>
                                    <Chip 
                                        label={epic.project}
                                        size="small"
                                        sx={{ 
                                            bgcolor: `${epic.color}20`,
                                            color: epic.color,
                                            fontWeight: 500,
                                            fontSize: '0.7rem',
                                            height: '20px'
                                        }}
                                    />
                                </TableCell>
                                <TableCell sx={{ py: 1 }}>
                                    <StatusChip label={epic.status} status={epic.status} size="small" />
                                </TableCell>
                                <TableCell sx={{ py: 1 }}>
                                    <PriorityChip label={epic.priority} priority={epic.priority} size="small" />
                                </TableCell>
                                <TableCell sx={{ py: 1 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <Box sx={{ width: '100%', mr: 1 }}>
                                            <LinearProgress 
                                                variant="determinate" 
                                                value={epic.progress}
                                                sx={{ 
                                                    height: 6,
                                                    borderRadius: 3,
                                                    bgcolor: '#e2e8f0',
                                                    '& .MuiLinearProgress-bar': {
                                                        borderRadius: 3,
                                                        background: epic.progress === 100 
                                                            ? '#10b981' 
                                                            : `linear-gradient(90deg, ${epic.color} 0%, ${epic.color}aa 100%)`
                                                    }
                                                }}
                                            />
                                        </Box>
                                        <Typography variant="caption" color="textSecondary" sx={{ minWidth: '28px', fontSize: '0.75rem' }}>
                                            {epic.progress}%
                                        </Typography>
                                    </Box>
                                </TableCell>
                                <TableCell sx={{ py: 1 }}>
                                    <Box>
                                        <Typography variant="caption" sx={{ display: 'block', fontSize: '0.75rem' }}>
                                            {formatDate(epic.startDate)}
                                        </Typography>
                                        <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.7rem' }}>
                                            to {formatDate(epic.endDate)}
                                        </Typography>
                                    </Box>
                                </TableCell>
                                {/* <TableCell sx={{ py: 1 }}>
                                    <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                                        {epic.completedStories}/{epic.totalStories}
                                    </Typography>
                                </TableCell> */}
                                <TableCell sx={{ py: 1 }}>
                                    <AvatarGroup max={3} sx={{ 
                                        '& .MuiAvatar-root': { 
                                            width: 24, 
                                            height: 24, 
                                            fontSize: 10,
                                            borderColor: 'white'
                                        }
                                    }}>
                                        {epic.assignees.map((assignee, index) => (
                                            <Tooltip key={index} title={assignee}>
                                                <Avatar sx={{ 
                                                    bgcolor: epic.color,
                                                    fontSize: 10
                                                }}>
                                                    {assignee.charAt(0)}
                                                </Avatar>
                                            </Tooltip>
                                        ))}
                                    </AvatarGroup>
                                </TableCell>
                                <TableCell sx={{ py: 1 }} onClick={(e) => e.stopPropagation()}>
                                    <Stack direction="row" spacing={0.5} justifyContent="center">
                                        <Tooltip title="View User Stories & Tasks">
                                            <IconButton
                                                size="small"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleShowUserStoryTaskTable(epic);
                                                }}
                                                sx={{
                                                    color: '#6366f1',
                                                    '&:hover': { bgcolor: '#eef2ff' },
                                                    padding: '4px'
                                                }}
                                            >
                                                <Article sx={{ fontSize: 16 }} />
                                            </IconButton>
                                        </Tooltip>

                                        <IconButton
                                            size="small"
                                            onClick={(e) => handleMenuOpen(e, epic)}
                                            sx={{
                                                padding: '4px',
                                                '&:hover': { bgcolor: '#f1f5f9' },
                                            }}
                                        >
                                            <MoreVert sx={{ fontSize: 16, color: '#64748b' }} />
                                        </IconButton>
                                    </Stack>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>

                {/* Options Menu */}
                <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleMenuClose}
                    PaperProps={{
                        elevation: 2,
                        sx: {
                            borderRadius: '6px',
                            minWidth: '140px',
                        }
                    }}
                >
                    {renderMenuItems()}
                </Menu>
            </TableContainer>

            {/* Compact Pagination */}
            {totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1.5 }}>
                    <Pagination
                        count={totalPages}
                        page={currentPage}
                        onChange={(event, page) => setCurrentPage(page)}
                        color="primary"
                        size="small"
                        siblingCount={0}
                        boundaryCount={1}
                        sx={{
                            '& .MuiPaginationItem-root': {
                                fontSize: '0.8rem',
                                minWidth: '28px',
                                height: '28px'
                            }
                        }}
                    />
                </Box>
            )}

            {/* Compact Empty State */}
            {filteredEpics.length === 0 && (
                <Box sx={{ 
                    textAlign: 'center', 
                    py: 4,
                    bgcolor: '#f8fafc',
                    borderRadius: 1,
                    mt: 1
                }}>
                    <Folder sx={{ fontSize: 40, color: '#cbd5e1', mb: 1 }} />
                    <Typography variant="body1" color="textSecondary" gutterBottom sx={{ fontSize: '0.9rem' }}>
                        No Epics Found
                    </Typography>
                    <Typography variant="caption" color="textSecondary" sx={{ mb: 2, display: 'block', fontSize: '0.8rem' }}>
                        {selectedCategory === 'all' 
                            ? 'There are no epics to display.' 
                            : `No epics with status "${selectedCategory}" found.`}
                    </Typography>
                    <Button 
                        variant="contained" 
                        size="small"
                        startIcon={<Add />}
                        sx={{ textTransform: 'none', fontSize: '0.85rem' }}
                    >
                        Create New Epic
                    </Button>
                </Box>
            )}
        </Box>
    );
};

export default EpicsComponentParent;